import { SwapType } from '@optimex-xyz/trade-wallet-sdk'

import { EphemeralWalletSigner } from '@/utils'

import { BytesLike, Wallet } from 'ethers'

import { IPair, IToken } from './token.type'

export interface AcrossFromMetadata {
  fromUserAddress: string
  fromNetworkId: bigint
  fromTokenId: string
}
export interface AcrossDeposit {
  sessionId: bigint
  solver: string
  amountIn: bigint
  from: AcrossFromMetadata
  to: [BytesLike, BytesLike, BytesLike]
}

export interface AcrossTermCondition {
  mpc: string
  ephemeralAssetAddress: string
  ephemeralL2Address: string
  refundAddress: string
  timeout: bigint
}

export interface AcrossDepositV3 {
  depositor: string
  recipient: string
  inputToken: string
  outputToken: string
  inputAmount: bigint
  outputAmount: bigint
  destinationChainId: bigint
  exclusiveRelayer: string
  quoteTimestamp: bigint
  fillDeadline: bigint
  exclusivityDeadline: bigint
  message: BytesLike
}

export interface AcrossFeeInfo {
  pct: string
  total: string
}

export interface AcrossLimits {
  minDeposit: string
  maxDeposit: string
  maxDepositInstant: string
  maxDepositShortDelay: string
  recommendedDepositInstant: string
}

export interface AcrossTokenInfo {
  address: string
  symbol: string
  decimals: number
  chainId: number
}

export interface AcrossSuggestedFeesRequest {
  originChainId: number
  destinationChainId: number
  inputToken: string
  outputToken: string
  amount: string
  recipient?: string
  message?: string
  relayer?: string
  timestamp?: number
  skipAmountLimit?: boolean
  allowUnmatchedDecimals?: boolean
}
export interface AcrossSuggestedFeesResponse {
  estimatedFillTimeSec: number
  capitalFeePct: string
  capitalFeeTotal: string
  relayGasFeePct: string
  relayGasFeeTotal: string
  relayFeePct: string
  relayFeeTotal: string
  lpFeePct: string
  timestamp: string
  isAmountTooLow: boolean
  quoteBlock: string
  exclusiveRelayer: string
  exclusivityDeadline: number
  spokePoolAddress: string
  destinationSpokePoolAddress: string
  totalRelayFee: AcrossFeeInfo
  relayerCapitalFee: AcrossFeeInfo
  relayerGasFee: AcrossFeeInfo
  lpFee: AcrossFeeInfo
  limits: AcrossLimits
  fillDeadline: string
  outputAmount: string
  inputToken: AcrossTokenInfo
  outputToken: AcrossTokenInfo
}

export enum AcrossSignatureType {
  BridgeOptimexAuth,
  BridgeUserAuth,
  BridgeMPCAuth,
  AcrossUserAuth,
  AcrossOptimexAuth,
  crossAgentAuth,
  FallbackTransfer,
  AcrossAgentAuth,
  AcrossL2RequestRefund,
  AcrossL2ConfirmRefund,
  AcrossL2RejectRefund,
}

export type BridgeType = 'BTC-EVM' | 'EVM-BTC'

export interface AcrossEvmDepositParams {
  sourceToken: IToken
  canonicalToken: IToken
  acrossToken?: IToken // Token needed for deposit to Across
  finalTargetToken?: IToken // Token out after bridge and swapped
  depositAmount: bigint
  senderAddress: string
  recipientAddress: string
  sessionId: bigint
  tradeId: string
  ephemeralSigner: EphemeralWalletSigner
  solverAddress: string
  mpcAssetChainPubkey: string
  scriptTimeout: bigint
  outputAmount: bigint
}

export interface DepositPreparationResult {
  termCondition: AcrossTermCondition
  depositData: any
  metadata: any
  ephemeralWallet: Wallet
}

export interface SignatureResult {
  userAuthSignature: string
  optimexAuthSignature: string
}

export interface DepositContractArgs {
  args: (bigint | BytesLike | Record<string, bigint>)[]
  value: bigint
}

export interface DepositExecutionData {
  acrossMessage: BytesLike
  depositParams: AcrossDepositV3
  contractArgs: DepositContractArgs
}

export type BridgeDirection = 'EVM_TO_BTC' | 'BTC_TO_EVM'
export interface BridgeInfo {
  direction: BridgeDirection
  sourceNetworkType: string
  targetNetworkType: string
  isSupported: boolean
  canonicalToken?: IToken
  acrossToken?: IToken
}

export interface TradePairInfo {
  pair: IPair | null
  swapType: SwapType | null
  isValidRoute: boolean
  isActiveRoute: boolean
  bridgeInfo?: BridgeInfo // Only present for bridge trades
}
